// The "which business unit is this for?" choice made at the top of a create
// flow (new estimate / new proposal / new job). It narrows the templates and
// job types offered, and travels with the record that flow creates.
//
// Zero-cost when unused is a hard invariant here:
//   - 0 active BUs  → `hasBusinessUnits` is false, callers render nothing, and
//                     `businessUnitId` stays null so no filter is ever applied.
//   - 1 active BU   → auto-selected, so the user never spends a click on a
//                     choice that has one answer. The control is still shown
//                     and still clearable (clearing removes the filter) —
//                     hiding it would make the auto-selection unclearable.
//   - 2+ active BUs → shown, defaulted to nothing selected (= unfiltered).
//
// Clearing sticks: once the user has touched the control we never re-apply the
// single-unit default, or "clear" would silently undo itself on the next render.
//
// The answer is DERIVED on every render, never reconciled by an effect: an
// effect runs after the first paint, so with a warm unit cache the raw URL id
// would be returned for one render before being dropped — long enough for a
// gated create flow to mount its form with an archived unit stamped on.
//
// The initial id arrives from a URL search param and is therefore arbitrary: a
// bookmarked or shared link can name a unit that has since been ARCHIVED. Such
// an id is dropped once the unit list arrives, because it would otherwise mean
// two contradictory things at once — the picker matches it to no option and
// shows "All business units", while the template query and the `carry` search
// params treat it as a live selection and stamp it onto the record the flow
// creates. Dropping it settles that on "no selection", the only reading a
// create flow can act on: new work cannot start in a retired unit.
import { useCallback, useState } from 'react';
import { useBusinessUnitOptions } from './useBusinessUnitOptions';

export interface BusinessUnitScopeSelection {
  /** The selected unit, or null for "no selection" — which means NO filtering. */
  businessUnitId: string | null;
  setBusinessUnitId: (value: string | null) => void;
  /** False while the tenant has no active units; callers render no step at all. */
  hasBusinessUnits: boolean;
  /**
   * True once the unit list has arrived OR the fetch has failed — the point at
   * which `hasBusinessUnits` can be believed. A flow that REQUIRES the choice
   * holds its gate closed until then: while the list is in flight it cannot
   * tell "this tenant has no units" from "the units have not arrived yet", and
   * letting the operator through on the second reading skips the question.
   * A failed fetch settles too, on "no units": the flow degrades to its
   * unscoped self rather than locking the operator out of creating anything.
   */
  isSettled: boolean;
}

/** The user's own choice, once made. `null` here means "cleared", not "unset". */
type Choice = { touched: false } | { touched: true; value: string | null };

export function useBusinessUnitScopeSelection(
  initialBusinessUnitId?: string | null,
): BusinessUnitScopeSelection {
  const { activeUnits, isLoaded, isError } = useBusinessUnitOptions();
  const [choice, setChoice] = useState<Choice>({ touched: false });
  // Read on every render, NOT captured at mount: the create-path gate stays
  // mounted while the picker it rendered navigates to `?manual=1&businessUnitId=…`,
  // and a captured value would leave it holding the old link's answer (none)
  // and re-rendering the picker forever. The user's own choice still wins.
  const initial = initialBusinessUnitId ?? null;
  const isSettled = isLoaded || isError;

  let businessUnitId: string | null;
  if (choice.touched) {
    businessUnitId = choice.value;
  } else if (!isSettled) {
    // Held optimistically so a valid deep link filters on the first render
    // instead of flashing the unfiltered list. A REQUIRED flow does not act on
    // it yet — it waits for `isSettled`.
    businessUnitId = initial;
  } else if (initial !== null && activeUnits.some((unit) => unit.id === initial)) {
    businessUnitId = initial;
  } else if (activeUnits.length === 1) {
    // One unit is not a choice — pre-pick it so nobody spends a click.
    businessUnitId = activeUnits[0].id;
  } else {
    // Archived, unknown, or unverifiable after a failed fetch: dropped, like
    // the header explains. On a failed fetch the list is empty, so this is
    // also how the flow degrades to UNSCOPED creation — an id nobody can
    // verify, or see (the step does not render without units), is never
    // stamped on.
    businessUnitId = null;
  }

  const setBusinessUnitId = useCallback((value: string | null) => {
    setChoice({ touched: true, value });
  }, []);

  return {
    businessUnitId,
    setBusinessUnitId,
    hasBusinessUnits: activeUnits.length > 0,
    isSettled,
  };
}
